import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { isUUID, generateCourseSlug } from "@/utils/slugUtils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SEOHead } from "@/components/SEOHead";
import { CountdownTimer } from "@/components/CountdownTimer";
import { RegistrationForm } from "@/components/RegistrationForm";
import { ResponsiveImage } from "@/components/ui/responsive-image";
import { createResponsiveImageProps } from "@/utils/imageOptimization";
import { 
  Clock, 
  Users, 
  Monitor, 
  Globe, 
  BookOpen,
  ChevronLeft,
  CheckCircle,
  User,
  CalendarDays,
  ArrowRight
} from "lucide-react";
import { formatArmenianDate } from "@/utils/dateFormatting";
import { useSettings } from "@/contexts/SettingsContext";
import { toast } from "sonner";
import { SocialShare } from "@/components/SocialShare";
import { CourseCodePanel, courseCodeFileName } from "@/components/CourseCodePanel";
import { CourseFAQ } from "@/components/CourseFAQ";
import { CourseSocialCard } from "@/components/seo/CourseSocialCard";
import { CourseStructuredData } from "@/components/seo/CourseStructuredData";

export default function CoursePage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { getSetting } = useSettings();

  const { data: course, isLoading, error } = useQuery({
    queryKey: ['course', courseId],
    queryFn: async () => {
      let query = supabase
        .from('courses')
        .select(`
          *,
          faculties (
            name,
            color,
            icon
          )
        `)
        .eq('is_active', true);

      // If courseId is a UUID, search by id, otherwise search by slug
      if (isUUID(courseId || '')) {
        query = query.eq('id', courseId);
      } else {
        // Search by name similarity for slug-based lookup
        // We'll need to generate the slug from the course name and compare
        const { data: courses, error: coursesError } = await supabase
          .from('courses')
          .select(`
            *,
            faculties (
              name,
              color,
              icon
            )
          `)
          .eq('is_active', true);

        if (coursesError) throw coursesError;
        
        // Find course by generating slug from course name
        const { generateCourseSlug } = await import('@/utils/slugUtils');
        const course = courses?.find(c => generateCourseSlug(c.name) === courseId);
        
        if (!course) {
          throw new Error('Course not found');
        }
        
        return course;
      }

      const { data, error } = await query.single();
      if (error) throw error;
      return data;
    },
    staleTime: 0, // Force fresh data
    refetchOnWindowFocus: true,
    refetchOnMount: true
  });


  const getFormatDisplay = (format: string) => {
    switch (format) {
      case 'online': return 'Օնլայն';
      case 'offline': return 'Առկա';
      case 'inPerson': return 'Առկա';
      case 'in_person': return 'Առկա';
      case 'hybrid': return 'Հիբրիդային';
      default: return format;
    }
  };

  const getLearningTypeDisplay = (type: string) => {
    switch (type) {
      case 'group': return 'Խմբային';
      case 'individual': return 'Անհատական';
      case 'both': return 'Երկուսն էլ';
      default: return type;
    }
  };

  const getStatusDisplay = (status: string) => {
    switch (status) {
      case "active": return "Ակտիվ";
      case "enrollment": return "Ընդունելություն";
      case "discount": return "Զեղչ";
      case "draft": return "Նախագիծ";
      case "full": return "Լրացված";
      case "completed": return "Ավարտված";
      default: return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
      case "enrollment": return "border-primary/30 bg-primary/10 text-primary";
      case "discount": return "border-primary/30 bg-secondary text-secondary-foreground";
      case "full": return "border-destructive/30 bg-destructive/10 text-destructive";
      case "draft":
      case "completed":
      default: return "border-border bg-muted text-muted-foreground";
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <div role="status" className="text-2xl font-bold">Բեռնվում է...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <div role="alert" className="text-2xl font-bold text-destructive">Դասընթացը չի գտնվել</div>
            <Link to="/" className="mt-4 inline-block">
              <Button variant="outline">Վերադարձ գլխավոր էջ</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Prefer the globally configured (real) start date when the course row still
  // carries an outdated date.
  const globalStart = getSetting('course_start_date');
  const globalStartTime = globalStart ? new Date(globalStart).getTime() : NaN;
  const courseStartTime = course.start_date ? new Date(course.start_date).getTime() : NaN;
  const effectiveStartDate =
    !Number.isNaN(globalStartTime) &&
    (Number.isNaN(courseStartTime) || courseStartTime < Date.now())
      ? globalStart
      : course.start_date;

  const globalDeadline = getSetting('admission_deadline');
  const courseDeadlineTime = course.admission_deadline ? new Date(course.admission_deadline).getTime() : NaN;
  const effectiveDeadline =
    globalDeadline && (Number.isNaN(courseDeadlineTime) || courseDeadlineTime < Date.now())
      ? globalDeadline
      : course.admission_deadline;

  return (
    <>
      <SEOHead 
        title={course.name}
        description={course.what_awaits_you || course.description || `Սովորեք ${course.name} դասընթացը Global IT Academy-ում: ${course.faculties?.name || 'Ծրագրավորման'} բաժնի փորձառու դասախոսների հետ: Ժամանակակից ծրագիր և պրակտիկ ուսուցում:`}
        path={`/course/${generateCourseSlug(course.name)}`}
        image={course.image_url}
        keywords={`${course.name}, դասընթաց, ${course.faculties?.name}, Global IT Academy, ծրագրավորում, IT ուսուցում, արհեստագիտություն, կարիերա, տեխնոլոգիական կրթություն, մասնագիտական վերապատրաստում, պրակտիկ նախագծեր`}
        contentType="course"
        price={course.group_price || course.individual_price}
        currency="AMD"
        availability="https://schema.org/InStock"
      />
      
      {/* Enhanced Social Media Card & Structured Data */}
      <CourseSocialCard 
        course={course}
        path={`/course/${generateCourseSlug(course.name)}`}
      />
      <CourseStructuredData 
        course={course}
        path={`/course/${generateCourseSlug(course.name)}`}
      />
      
      <div className="min-h-screen overflow-x-hidden bg-background text-foreground">
        {/* Hero Section */}
        <section className="relative overflow-hidden border-b border-border/60 bg-card/30 pb-10 pt-5 sm:pb-14 sm:pt-7 lg:pb-20 lg:pt-10">
          <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border)/0.18)_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--border)/0.18)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:linear-gradient(to_bottom,black,transparent_88%)]" aria-hidden="true" />
          <div className="pointer-events-none absolute inset-y-0 right-0 hidden w-1/2 bg-primary/5 lg:block" aria-hidden="true" />
          <div className="app-container relative max-w-6xl px-4 sm:px-6 lg:px-8">
            <Link to="/" className="mb-8 inline-flex min-h-10 items-center gap-1 text-sm font-medium text-muted-foreground transition-colors hover:text-primary">
              <ChevronLeft className="h-4 w-4" />
              {"\u054E\u0565\u0580\u0561\u0564\u0561\u0580\u0571"}
            </Link>

            <div className="grid w-full grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-14">
              <div className="min-w-0 w-full text-left">
                <h1 className="max-w-2xl break-words font-display text-[34px] font-bold leading-tight text-foreground sm:text-[46px] lg:text-[58px]">
                  {course.name}
                </h1>
                
                {(course.description || course.what_awaits_you) && (
                  <p className="mt-5 max-w-2xl text-base leading-7 text-muted-foreground sm:text-lg">
                    {course.description || course.what_awaits_you}
                  </p>
                )}

                <div className="mt-7 grid max-w-md grid-cols-3 gap-2">
                  {course.format?.map((format) => (
                    <Badge key={format} variant="outline" className="min-w-0 justify-center gap-1.5 truncate border-border/70 bg-background/60 text-center text-foreground">
                      <Monitor className="h-3.5 w-3.5 shrink-0 text-primary" />
                      <span className="truncate">{getFormatDisplay(format)}</span>
                    </Badge>
                  ))}
                  {course.languages?.map((lang) => (
                    <Badge key={lang} variant="outline" className="min-w-0 justify-center gap-1.5 truncate border-border/70 bg-background/60 text-center text-foreground">
                      <Globe className="h-3.5 w-3.5 shrink-0 text-primary" />
                      <span className="truncate">{lang}</span>
                    </Badge>
                  ))}
                </div>

                <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                  <RegistrationForm 
                    prefilledCourse={course.name}
                    prefilledFaculty={course.faculties?.name}
                  >
                    <Button 
                      size="lg" 
                      className="min-h-12 w-full px-6 text-base sm:w-auto"
                    >
                      {"\u0533\u0580\u0561\u0576\u0581\u057E\u0565\u056C \u0564\u0561\u057D\u0568\u0576\u0569\u0561\u0581\u056B\u0576"}
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </RegistrationForm>
                  <Button asChild variant="outline" size="lg" className="min-h-12 w-full px-6 text-base sm:w-auto">
                    <a href="#curriculum">{"\u0548\u0582\u057D\u0578\u0582\u0574\u0576\u0561\u056F\u0561\u0576 \u056E\u0580\u0561\u0563\u056B\u0580"}</a>
                  </Button>
                </div>

                {effectiveDeadline && (
                  <div className="mt-7 max-w-xl">
                    <CountdownTimer deadline={effectiveDeadline} />
                  </div>
                )}
              </div>

              <div className="relative flex min-h-[340px] w-full items-center justify-center sm:min-h-[430px]">

                <div className="w-full max-w-xl">
                  <div className="mb-5 flex flex-wrap items-center gap-3">
                    {course.status && course.status !== 'none' && (
                      <Badge variant="outline" className={getStatusColor(course.status)}>
                        <span className="mr-2 h-1.5 w-1.5 rounded-full bg-current" />
                        {getStatusDisplay(course.status)}
                      </Badge>
                    )}
                    {course.faculties?.name && (
                      <span className="text-sm text-muted-foreground">{course.faculties.name}</span>
                    )}
                  </div>

                  {effectiveStartDate && (
                    <div className="mb-5 flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="h-4 w-4 text-primary" />
                      <span>{"\u0544\u0565\u056F\u0576\u0561\u0580\u056F\u0568\u055D"}</span>
                      <strong className="font-semibold text-foreground">{formatArmenianDate(effectiveStartDate)}</strong>
                    </div>
                  )}

                  <div className="relative w-full overflow-hidden rounded-lg border border-border/70 bg-card shadow-xl shadow-primary/10">
                  <div className="flex h-12 items-center gap-2 border-b border-border/70 bg-muted/70 px-4">
                    <span className="h-2.5 w-2.5 rounded-full bg-destructive" />
                    <span className="h-2.5 w-2.5 rounded-full bg-primary/50" />
                    <span className="h-2.5 w-2.5 rounded-full bg-primary" />
                    <span className="ml-3 text-xs font-medium text-muted-foreground">{courseCodeFileName(course.name)}</span>
                  </div>
                  <div className="grid min-h-[280px] grid-cols-[1fr_auto] sm:min-h-[340px]">
                    <div className="min-w-0 p-5 sm:p-7">
                      <CourseCodePanel courseName={course.name} />
                    </div>
                    <div className="flex w-24 items-center justify-center border-l border-border/60 bg-primary/5 p-3 sm:w-40 sm:p-5">
                      {course.image_url ? (
                        <ResponsiveImage
                          {...createResponsiveImageProps(
                            course.image_url,
                            course.name,
                            "courseDetail",
                            "h-auto w-full bg-transparent object-contain drop-shadow-xl"
                          )}
                        />
                      ) : (
                        <BookOpen className="h-14 w-14 text-primary/50" />
                      )}
                    </div>
                  </div>
                </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-border/60 bg-background">
          <div className="app-container grid max-w-6xl grid-cols-2 gap-px overflow-hidden border-x border-border/60 bg-border/60 sm:grid-cols-4">
            <div className="min-w-0 bg-background p-4 sm:p-5">
              <Clock className="mb-3 h-5 w-5 text-primary" />
              <p className="text-xs text-muted-foreground">{"\u054F\u0587\u0578\u0572\u0578\u0582\u0569\u0575\u0578\u0582\u0576"}</p>
              <p className="mt-1 break-words font-display text-lg font-bold">{course.duration_months} {"\u0561\u0574\u056B\u057D"}</p>
            </div>
            <div className="min-w-0 bg-background p-4 sm:p-5">
              <CalendarDays className="mb-3 h-5 w-5 text-primary" />
              <p className="text-xs text-muted-foreground">{"\u0540\u0561\u0573\u0561\u056D\u0561\u056F\u0561\u0576\u0578\u0582\u0569\u0575\u0578\u0582\u0576"}</p>
               <p className="mt-1 break-words font-display text-lg font-bold">{"\u0547\u0561\u0562\u0561\u0569\u0561\u056F\u0561\u0576"} {course.weekly_frequency || 3} {"\u0585\u0580"}</p>
            </div>
            <div className="min-w-0 bg-background p-4 sm:p-5">
              <Monitor className="mb-3 h-5 w-5 text-primary" />
              <p className="text-xs text-muted-foreground">{"\u0541\u0587\u0561\u0579\u0561\u0583"}</p>
              <p className="mt-1 break-words font-display text-lg font-bold">{course.format?.map(format => getFormatDisplay(format)).join(', ')}</p>
            </div>
            <div className="min-w-0 bg-background p-4 sm:p-5">
              <Users className="mb-3 h-5 w-5 text-primary" />
              <p className="text-xs text-muted-foreground">{"\u0548\u0582\u057D\u0578\u0582\u0581\u0574\u0561\u0576 \u057F\u0565\u057D\u0561\u056F"}</p>
              <p className="mt-1 break-words font-display text-lg font-bold">{course.learning_type?.map(type => getLearningTypeDisplay(type)).join(', ')}</p>
            </div>
          </div>
        </section>

        {/* Course Details */}
        <section className="py-12 md:py-16 lg:py-20">
          <div className="app-container max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-start">
              {/* Main Content */}
              <div className="order-2 min-w-0 space-y-10 lg:order-1 lg:col-span-8">
                {/* What Awaits You */}
                {course.what_awaits_you && (
                  <section className="border-b border-border/60 pb-10">
                    <span className="section-kicker">{"\u0534\u0561\u057D\u0568\u0576\u0569\u0561\u0581\u056B \u0574\u0561\u057D\u056B\u0576"}</span>
                    <h2 className="mt-2 font-display text-3xl font-bold text-foreground">{"\u053B\u0576\u0579 \u0567 \u0571\u0565\u0566 \u057D\u057A\u0561\u057D\u057E\u0578\u0582\u0574"}</h2>
                    <p className="mt-5 text-base leading-8 text-muted-foreground sm:text-lg">
                        {course.what_awaits_you}
                    </p>
                  </section>
                )}

                {/* Curriculum Phases */}
                {course.curriculum_phases && Array.isArray(course.curriculum_phases) && course.curriculum_phases.length > 0 && (
                  <section id="curriculum" className="scroll-mt-28">
                    <div className="mb-6 flex items-end justify-between gap-4 border-b border-border/60 pb-5">
                      <div>
                        <span className="section-kicker">{"\u053E\u0580\u0561\u0563\u0580\u056B \u0583\u0578\u0582\u056C\u0565\u0580"}</span>
                        <h2 className="mt-2 font-display text-3xl font-bold text-foreground">{"\u0548\u0582\u057D\u0578\u0582\u0574\u0576\u0561\u056F\u0561\u0576 \u0573\u0561\u0576\u0561\u057A\u0561\u0580\u0570"}</h2>
                      </div>
                      <Badge variant="outline" className="hidden shrink-0 sm:inline-flex">{Array.isArray(course.curriculum_phases) ? course.curriculum_phases.length : 0} {"\u0583\u0578\u0582\u056C"}</Badge>
                    </div>
                      <Accordion type="single" collapsible className="space-y-3">
                        {course.curriculum_phases.map((phase: any, index: number) => (
                          <AccordionItem key={index} value={`phase-${index}`} className="overflow-hidden rounded-lg border border-border/70 bg-card/70 px-4 transition-colors data-[state=open]:border-primary/40 sm:px-5">
                            <AccordionTrigger className="min-h-16 gap-4 py-4 text-left hover:no-underline">
                              <span className="flex min-w-0 items-center gap-4">
                                <span className="font-display text-sm font-bold text-primary">{String(index + 1).padStart(2, '0')}</span>
                                <span className="break-words text-base font-semibold text-foreground sm:text-lg">{phase.name}</span>
                              </span>
                            </AccordionTrigger>
                            <AccordionContent className="border-t border-border/60 pb-5 pt-5">
                              <div className="space-y-3 pl-0 sm:pl-10">
                                {phase.topics?.map((topic: string, topicIndex: number) => (
                                  <div key={topicIndex} className="flex items-start gap-3">
                                    <CheckCircle className="mt-1 h-4 w-4 flex-shrink-0 text-primary" />
                                    <span className="break-words leading-relaxed text-muted-foreground">{topic}</span>
                                  </div>
                                ))}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                  </section>
                )}

                {/* Curriculum */}
                {course.curriculum && (
                  <Card id={Array.isArray(course.curriculum_phases) && course.curriculum_phases.length > 0 ? undefined : "curriculum"} className="scroll-mt-28 border-border/70 bg-card/70">
                    <CardHeader>
                      <CardTitle className="flex items-center">
                        <BookOpen className="w-6 h-6 mr-2 text-primary" />
                        Ուսումնական ծրագիր
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="prose prose-slate dark:prose-invert max-w-none">
                        <p className="whitespace-pre-line text-muted-foreground leading-relaxed">
                          {course.curriculum}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Instructors */}
                {course.instructors && course.instructors.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center">
                        <User className="w-6 h-6 mr-2 text-primary" />
                        Դասախոսներ
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid sm:grid-cols-2 gap-4">
                        {course.instructors.map((instructor, index) => (
                          <div key={index} className="flex items-center p-4 bg-muted/30 rounded-lg">
                            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mr-4">
                              <User className="w-6 h-6 text-primary" />
                            </div>
                            <div>
                              <h4 className="font-semibold">{instructor}</h4>
                              <p className="text-sm text-muted-foreground">Մասնագետ դասախոս</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                <CourseFAQ
                  courseName={course.name}
                  facultyName={course.faculties?.name}
                  durationMonths={course.duration_months}
                  weeklyFrequency={course.weekly_frequency}
                  formats={course.format}
                  learningTypes={course.learning_type}
                  languages={course.languages}
                  individualPrice={course.individual_price}
                  groupPrice={course.group_price}
                  startDate={effectiveStartDate}
                  deadline={effectiveDeadline}
                  instructors={course.instructors}
                  path={`/course/${generateCourseSlug(course.name)}`}
                />
              </div>


              {/* Sidebar */}
              <aside className="order-1 space-y-5 lg:order-2 lg:col-span-4 lg:sticky lg:top-24">
                {/* Quick Info */}
                <Card className="overflow-hidden border-primary/25 bg-card/90 shadow-lg shadow-primary/5">
                  <CardHeader>
                    <span className="section-kicker">{"\u0533\u056B\u0576 \u0587 \u057A\u0561\u0575\u0574\u0561\u0576\u0576\u0565\u0580"}</span>
                    <CardTitle className="font-display text-2xl">{"\u0534\u0561\u057D\u0568\u0576\u0569\u0561\u0581\u056B \u0574\u0561\u0576\u0580\u0561\u0574\u0561\u057D\u0576\u0565\u0580"}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {(course.individual_price || course.group_price) && (
                      <div className="space-y-3">
                        {course.individual_price && (
                          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                            <span className="text-muted-foreground">Անհատական գին</span>
                            <span className="font-semibold text-lg text-primary">{course.individual_price.toLocaleString()} դր․/ամիս</span>
                          </div>
                        )}
                        {course.group_price && (
                          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                            <span className="text-muted-foreground">Խմբային գին</span>
                            <span className="font-semibold text-lg text-primary">{course.group_price.toLocaleString()} դր․/ամիս</span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    <Separator />
                    
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                      <span className="text-muted-foreground">Տևողություն</span>
                      <span className="font-medium">
                        {course.duration_months} ամիս
                        {course.duration_months === 6 && " (Բազային)"}
                        {course.duration_months === 9 && " (Խորացված)"}
                        {course.duration_months === 18 && " (Ակադեմիական)"}
                      </span>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                      <span className="text-muted-foreground">Հաճախություն</span>
                      <span className="font-medium">Շաբաթական {course.weekly_frequency || 3} անգամ</span>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                      <span className="text-muted-foreground">Դասի տևողություն</span>
                      <span className="font-medium">1.5-2 ժամ</span>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                      <span className="text-muted-foreground">Ձևաչափ</span>
                      <span className="font-medium text-right">{course.format?.map(format => getFormatDisplay(format)).join(', ')}</span>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                      <span className="text-muted-foreground">Տիպ</span>
                      <span className="font-medium text-right">{course.learning_type?.map(type => getLearningTypeDisplay(type)).join(', ')}</span>
                    </div>

                    {effectiveDeadline && (
                      <>
                        <Separator />
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                          <span className="text-muted-foreground">Ընդունելության ժամկետ</span>
                            <span className="text-right font-medium text-primary">
                            {formatArmenianDate(effectiveDeadline)}
                          </span>
                        </div>
                      </>
                    )}

                    {effectiveStartDate && (
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                        <span className="text-muted-foreground">Սկիզբ</span>
                        <span className="font-medium text-right">{formatArmenianDate(effectiveStartDate)}</span>
                      </div>
                    )}

                    {course.languages && course.languages.length > 0 && (
                      <>
                        <Separator />
                        <div>
                          <span className="text-muted-foreground block mb-2">Լեզուներ</span>
                          <div className="flex flex-wrap gap-2">
                            {course.languages.map((lang, index) => (
                              <Badge key={index} variant="secondary" className="text-xs">
                                {lang}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </>
                    )}

                    <RegistrationForm 
                      prefilledCourse={course.name}
                      prefilledFaculty={course.faculties?.name}
                    >
                      <Button size="lg" className="mt-2 min-h-12 w-full">
                        {"\u0533\u0580\u0561\u0576\u0581\u057E\u0565\u056C \u0570\u056B\u0574\u0561"}
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    </RegistrationForm>
                  </CardContent>
                </Card>

                {/* Enhanced Social Share */}

                <SocialShare 
                  title={course.name}
                  description={course.what_awaits_you || course.description || `Սովորեք ${course.name} մեր փորձառու դասախոսների հետ`}
                  hashtags={['ծրագրավորում', 'ITուսուցում', 'կարիերա', 'տեխնոլոգիա']}
                  price={course.group_price}
                  courseData={{
                    faculty: course.faculties?.name,
                    duration: course.duration_months,
                    format: course.format,
                    status: course.status
                  }}
                  course={course}
                />
              </aside>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}